import { motion } from 'framer-motion'

const steps = [
  {
    title: 'Choose Your Destination',
    description:
      "Select your pickup location and destination, whether it's an airport, hotel, ski resort or day trip.",
    image: '/assets/images/how-step-1.png',
    bg: '#DBEFB0',
    position: 'top-left' as const,
    desktop: {
      left: '8%',
      top: '14%',
      textSide: 'right' as const,
      textAlign: 'left' as const,
      textMaxWidth: '360px',
    },
  },
  {
    title: 'Select Your Vehicle',
    description:
      'Choose the perfect vehicle for your travel needs, from sedans to spacious vans for groups.',
    image: '/assets/images/how-step-2.png',
    bg: '#283F47',
    position: 'bottom-left' as const,
    desktop: {
      left: '33%',
      top: '40%',
      textSide: 'left' as const,
      textAlign: 'center' as const,
      textMaxWidth: '360px',
    },
  },
  {
    title: 'Confirm Your Booking',
    description:
      'Enter your travel details, receive instant confirmation, and relax knowing everything is arranged.',
    image: '/assets/images/how-step-3.png',
    bg: '#677A3C',
    position: 'top-right' as const,
    desktop: {
      left: '57%',
      top: '50%',
      textSide: 'right' as const,
      textAlign: 'left' as const,
      textMaxWidth: '330px',
    },
  },
  {
    title: 'Enjoy Your Journey',
    description:
      'Enter your travel details, receive instant confirmation, and relax knowing everything is arranged.',
    image: '/assets/images/how-step-4.png',
    bg: '#365E92',
    position: 'bottom-right' as const,
    desktop: {
      left: '88%',
      top: '82%',
      textSide: 'left' as const,
      textAlign: 'center' as const,
      textMaxWidth: '340px',
    },
  },
]

export default function HowItWorks() {
  return (
    <section className="mx-auto max-w-page px-4 py-16 lg:px-[86px] lg:py-20 overflow-hidden">
      <motion.div 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={{
          visible: {
            transition: {
              staggerChildren: 0.15,
            },
          },
        }}
      >
        <motion.div 
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <div className="mb-2 inline-flex h-[27px] items-center rounded-xl border border-[#C8C8C8] bg-surface-gray px-2.5">
              <span className="font-host text-xs font-medium text-brand-orange">How It Works</span>
            </div>
            <h2 className="mt-3 font-host text-[28px] font-medium leading-[1.08] tracking-[-0.02em] text-[#343434] sm:text-[32px]">
              Book Your Ride in 4 Simple Steps
            </h2>
          </div>
          <p className="max-w-[311px] text-right font-host text-base leading-[1.5] tracking-[-0.022em] text-black">
            From booking to arrival, we make your journey easy, comfortable, and stress-free.
          </p>
        </motion.div>

        <motion.div 
          variants={{
            hidden: { opacity: 0, scale: 0.95 },
            visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
          }}
          className="relative overflow-hidden rounded-3xl bg-white p-6 shadow-section lg:h-[682px] lg:p-12"
        >
          {/* Mobile stack */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="relative lg:hidden"
          >
            {steps.map((step, index) => {
              const isLast = index === steps.length - 1

              return (
                <div
                  key={step.title}
                  className={`relative ${isLast ? '' : 'pb-8'}`}
                >
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: {
                          duration: 0.45,
                          ease: 'easeOut',
                          delay: index * 0.95,
                        },
                      },
                    }}
                    className="relative z-10 flex gap-4"
                  >
                    <div
                      className="flex h-[67px] w-[67px] shrink-0 items-center justify-center rounded-[42px]"
                      style={{ backgroundColor: step.bg }}
                    >
                      <img src={step.image} alt="" className="h-12 w-12 object-contain" />
                    </div>

                    <div>
                      <h3 className="font-host text-lg font-semibold text-black">{step.title}</h3>
                      <p className="mt-1 font-host text-base leading-[1.4] text-black">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>

                  {!isLast && (
                    <motion.div
                      variants={{
                        hidden: { scaleY: 0, opacity: 0 },
                        visible: {
                          scaleY: 1,
                          opacity: 1,
                          transition: {
                            duration: 0.5,
                            ease: 'easeInOut',
                            delay: index * 0.95 + 0.42,
                          },
                        },
                      }}
                      className="pointer-events-none absolute bottom-0 left-[33px] top-[67px] z-0 origin-top border-l-2 border-dashed border-[#B5B5B5]"
                    />
                  )}
                </div>
              )
            })}
          </motion.div>

          {/* Desktop exact placement */}
          <motion.div 
            variants={{
              visible: {
                transition: { staggerChildren: 0.45, delayChildren: 0.3 }
              }
            }}
            className="relative hidden h-full w-full lg:block"
          >
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 1312 700"
              fill="none"
              preserveAspectRatio="none"
            >
              <mask id="dashed-line-mask">
                <motion.path
                  variants={{
                    hidden: { pathLength: 0 },
                    visible: { pathLength: 1, transition: { duration: 2.2, ease: "easeInOut" } }
                  }}
                  d="M 24 8
                     C 160 120, 300 220, 440 275
                     S 700 360, 860 405
                     S 1080 470, 1290 694"
                  stroke="white"
                  strokeWidth="6"
                  fill="none"
                />
              </mask>
              <path
                d="M 24 8
                   C 160 120, 300 220, 440 275
                   S 700 360, 860 405
                   S 1080 470, 1290 694"
                stroke="rgba(0,0,0,0.41)"
                strokeWidth="1"
                strokeDasharray="10 10"
                fill="none"
                mask="url(#dashed-line-mask)"
              />
            </svg>

            {steps.map((step) => {
              const desktop = step.desktop
              const isLeft = desktop.textSide === 'left'

              return (
                <motion.div
                  key={step.title}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                  }}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: desktop.left,
                    top: desktop.top,
                  }}
                >
                  <div className="relative">
                    <div
                      className="flex h-[67px] w-[67px] items-center justify-center rounded-[42px] shadow-[0_0_0_0_rgba(0,0,0,0)]"
                      style={{ backgroundColor: step.bg }}
                    >
                      <img src={step.image} alt="" className="h-12 w-12 object-contain" />
                    </div>

                    <div
                      className={[
                        'absolute top-1/2 w-[360px] -translate-y-1/2 font-host text-black',
                        isLeft ? 'right-[88px] text-center' : 'left-[88px] text-left',
                      ].join(' ')}
                      style={{ maxWidth: desktop.textMaxWidth }}
                    >
                      <h3 className="text-lg font-semibold leading-tight">{step.title}</h3>
                      <p className="mt-1 text-base leading-[1.45] tracking-[-0.01em]">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  )
}